/**
 * v3.ofac — OFAC archive-match suppression cross-validation surface.
 *
 * Bill 2026-05-09 — Wave-3 SDN cache mirrors Treasury's CURRENT sanctions
 * list. Today JHA OFACSrch returns hits without telling us whether the hit
 * is still on the active list or was archived/delisted by Treasury. This
 * domain calls our own SDN cache (POST /v3/ofac/classify-hits) to classify
 * each AliasId, so the FSR sees:
 *
 *   - 'current'        — still on Treasury's SDN list → block (existing rule)
 *   - 'archived-only'  — historical record, removed from active list → allow
 *                        with audit trail visible to Risk Mgmt
 *   - 'unknown'        — cache unreachable; treat as 'current' (fail-safe)
 *
 * Pairs with the legacy `client.jxrl.ofacSrch` JHA call — that stays. This
 * adds a SECOND step that enriches each AliasId with current/archived-only
 * classification so the cart-submit gate can route differently.
 *
 * Backend contract (Wave-3 Agent A, parallel build):
 *   POST /api/v3/ofac/classify-hits
 *   Body: { aliasIds: string[] }     (1-100 ids)
 *   Response: { success, classifications: OfacClassification[] }
 */

import type { RequestOptions } from '@retail-ops/shared-types';
import type { ApiHttpClient } from '../http.js';

// ----------------------------------------------------------------------------
// Types — match the LOCKED API CONTRACT shipped by backend Agent A.
// ----------------------------------------------------------------------------

/**
 * Tri-state per-hit classification. `unknown` = cache unreachable; the
 * frontend treats it as `current` (fail-safe block) so a transient cache
 * outage never silently allows a sanctioned name through.
 */
export type OfacClassificationKind = 'current' | 'archived-only' | 'unknown';

/**
 * Per-AliasId enrichment row returned by the classify-hits endpoint.
 *
 * Field semantics (locked with backend Agent A 2026-05-09):
 *   - `aliasId`         — echoes the AliasId we asked about (string).
 *   - `classification`  — see {@link OfacClassificationKind}.
 *   - `isCurrentSdn`    — `classification === 'current'` (convenience bool).
 *   - `entityId`        — Treasury SLS entity-id when found; null when
 *                         archived-only or unknown can't resolve.
 *   - `primaryName`     — canonical SDN primary name from the cache (for
 *                         display next to the AliasName JHA returned).
 *   - `programs`        — sanction programs (e.g. "IRAQ2", "SDGT", "CUBA").
 *                         Surfaced as a small program code chip per match.
 *   - `legalAuthorities`— EO citations / public-law refs.
 *   - `dob`             — date-of-birth feature, ISO when present.
 *   - `placeOfBirth`    — place-of-birth feature.
 *   - `gender`          — gender feature.
 *   - `akaResolved`     — true when the JHA hit matched an AKA (not the
 *                         primary name); drives the "Alias / AKA match"
 *                         label on the per-match card.
 *   - `akaName`         — the AKA literal we matched on (when resolved).
 *   - `akaType`         — JHA AKA type ("a.k.a." / "f.k.a." / "n.k.a.").
 *   - `akaCategory`     — AKA category enum from the SDN feed.
 */
export interface OfacClassification {
  aliasId: string;
  classification: OfacClassificationKind;
  isCurrentSdn: boolean;
  entityId: string | null;
  primaryName: string | null;
  programs: ReadonlyArray<string>;
  legalAuthorities: ReadonlyArray<string>;
  dob: string | null;
  placeOfBirth: string | null;
  gender: string | null;
  akaResolved: boolean;
  akaName: string | null;
  akaType: string | null;
  akaCategory: string | null;
}

export interface OfacClassifyRequest {
  /** 1-100 JHA OFACSrch AliasIds to classify against the local SDN cache. */
  aliasIds: ReadonlyArray<string>;
}

export interface OfacClassifyResponse {
  success: true;
  classifications: ReadonlyArray<OfacClassification>;
}

// ----------------------------------------------------------------------------
// Sanctions screening on the bank's own government lists (Bill 2026-10-19)
//   POST /api/v3/ofac/screen  { name, nameType, birthDate?, country?, cartId?, personKey? }
// Our lists decide (sanctions.authority = pcb); Core Director's OFACSrch runs
// on the server as the forecast of its nightly report and comes back in
// coreMatches / coreClassifications (the same shapes the JHA path used).
// ----------------------------------------------------------------------------

export type SanctionsVerdict = 'clear' | 'review' | 'hit' | 'pending';
export type SanctionsBand = 'hit' | 'review' | 'cleared-by-detail';

export interface SanctionsCandidate {
  sourceId: string;
  sourceLabel: string;
  role: 'decide' | 'review' | 'off';
  sourceEntryId: string;
  entryType: string;
  primaryName: string;
  matchedName: string;
  matchedKind: string;
  weakAlias: boolean;
  score: number;
  band: SanctionsBand;
  lists: ReadonlyArray<string>;
  programs: ReadonlyArray<string>;
  birthDates: ReadonlyArray<string>;
  countries: ReadonlyArray<string>;
  dobComparison: 'same-year' | 'different' | null;
  countryComparison: 'same' | 'different' | null;
}

/** One Core Director hit, labelled against our lists. */
export interface SanctionsForecastHit {
  aliasId: string;
  aliasName: string;
  matchType: string;
  entityId: string | null;
  assessment: 'also-our-match' | 'delisted' | 'not-our-match' | 'unaccounted';
  explanation: string;
}

export interface SanctionsSourceStatus {
  sourceId: string;
  label: string;
  role: 'decide' | 'review' | 'off';
  entries: number;
  lastSuccessAt: string | null;
  publisherDataAsOf: string | null;
  fresh: boolean;
}

export interface SanctionsCoreMatch {
  matchType: string;
  aliasName: string;
  aliasId: string;
  matchRef: string;
}

export interface SanctionsScreenResult {
  screeningId: string;
  verdict: SanctionsVerdict;
  /** The status written to the application: clear | hit-current | pending (| archived-only under Core authority). */
  status: string;
  authority: 'pcb' | 'core';
  topScore: number;
  summary: string;
  candidates: ReadonlyArray<SanctionsCandidate>;
  forecast: ReadonlyArray<SanctionsForecastHit>;
  forecastPath: string;
  sources: ReadonlyArray<SanctionsSourceStatus>;
  matcherVersion: string;
  coreMatches: ReadonlyArray<SanctionsCoreMatch>;
  coreClassifications: ReadonlyArray<OfacClassification>;
}

export interface SanctionsScreenRequest {
  name: string;
  nameType: 'Indv' | 'Org';
  birthDate?: string;
  country?: string;
  cartId?: string;
  personKey?: string;
}

export interface SanctionsScreenResponse {
  success: true;
  result: SanctionsScreenResult;
}

// ----------------------------------------------------------------------------
// Client
// ----------------------------------------------------------------------------

export class OfacApi {
  public constructor(private readonly http: ApiHttpClient) {}

  /**
   * Classify a batch of AliasIds against the local SDN cache. Pass the
   * AliasIds JHA OFACSrch returned; the response enriches each with a
   * `classification` so the FSR-facing UI can route current vs. archived-
   * only matches differently (red block vs. amber allow-with-audit).
   *
   * Request constraints (server-enforced):
   *   - 1 <= aliasIds.length <= 100
   *   - each AliasId is a non-empty string
   *
   * Failure modes:
   *   - cache unreachable → entries return classification='unknown'; the
   *     frontend treats those as 'current' (fail-safe block).
   *   - HTTP failure → caller sees ApiError; consumer should fall back to
   *     'current' for every AliasId so we never silently allow a hit
   *     through on a transient outage.
   */
  /**
   * Screen one name on the bank's own copy of the government sanctions lists
   * (OFAC SDN + consolidated decide; U.S. Commerce/State, UN, EU, UK, Canada,
   * Australia inform review — all set by Risk). Any signed-in user.
   * HTTP failure → ApiError; the caller records 'pending' (never 'clear').
   */
  public screen(body: SanctionsScreenRequest, opts?: RequestOptions): Promise<SanctionsScreenResponse> {
    return this.http.post<SanctionsScreenResponse>('/v3/ofac/screen', { ...opts, body });
  }

  /**
   * GET /v3/sanctions/screenings/{id}/report.pdf — the Sanctions Screening report for one screening
   * (CHG-0141, Bill 2026-11-02). Returns a Blob for a download link.
   */
  public getScreeningReportPdf(screeningId: string, opts?: RequestOptions): Promise<Blob> {
    return this.http.getBlob(`/v3/sanctions/screenings/${encodeURIComponent(screeningId)}/report.pdf`, { ...opts });
  }

  /**
   * POST /v3/ofac/screenings/{id}/decision — the officer's decision on a held screening when Risk has set
   * "officer first" (Jessica Beucler 2026-11-02): cleared (not the listed party), confirmed (a true match) or
   * escalated (send to Risk Management). A reason is required.
   */
  public decideScreening(
    screeningId: string,
    decision: 'cleared' | 'confirmed' | 'escalated',
    reason: string,
    opts?: RequestOptions,
  ): Promise<{ success: boolean; error?: string; decision?: { decisionId: string; screeningId: string; decision: string; reason: string; decidedBy: string; decidedAt: string; decidedAs: string } }> {
    return this.http.post(`/v3/ofac/screenings/${encodeURIComponent(screeningId)}/decision`, { ...opts, body: { decision, reason } });
  }

  public classifyHits(
    aliasIds: ReadonlyArray<string>,
    opts?: RequestOptions,
  ): Promise<OfacClassifyResponse> {
    const body: OfacClassifyRequest = { aliasIds };
    return this.http.post<OfacClassifyResponse>('/v3/ofac/classify-hits', {
      ...opts,
      body,
    });
  }
}
